import { Switch } from "@mui/material";
import { useEffect, useState } from "react";
import { IoIosInformationCircleOutline } from "react-icons/io";
import { RxCross1 } from "react-icons/rx";
import { getApi, putApi } from "../AppApis";
import {
    accessTypeArray,
    appointmentDurationsArray,
    appointmentTypes,
    availabilityArray,
    buffetTimesBetweenShowingsArray,
    maxAppointmentLengthArray,
    minimumNoticeRequiredArray,
} from "../utils/ShowingSettingsArrays";
import { toast } from "react-toastify";

export default function ShowingSettings({ onClose, property }) {
    const [appointmentType, setAppointmentType] = useState("");
    const [appointmentDuration, setAppointmentDuration] = useState("15");
    const [minimumNoticeRequired, setMinimumNoticeRequired] = useState("");
    const [weeklySchedule, setWeeklySchedule] = useState(availabilityArray);
    const [maxAppointmentLength, setMaxAppointmentLength] = useState("");
    const [isPauseAvailability, setIsPauseAvailability] = useState(false);
    const [accessType, setAccessType] = useState("LOCKBOX_WITH_CODE");
    const [approvalMethod, setApprovalMethod] = useState("AUTO_APPROVE");
    const [accessInstructions, setAccessInstructions] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const [autoDenyRequestsOutsideHours, setAutoDenyRequestsOutsideHours] = useState(false);
    const [notificationPreferences, setNotificationPreferences] = useState({
        sms: true,
        email: true,
        pushNotification: true,
        inAppOnly: false,
    });
    const [showingInstructions, setShowingInstructions] = useState({
        preShowing: "",
        postShowing: "",
        onSite: "",
    });
    const [temporaryUnavailability, setTemporaryUnavailability] = useState({
        startDate: "",
        endDate: "",
        notes: "",
    });

    // Helper function to get today's date in YYYY-MM-DD format
    const getTodayDate = () => {
        const today = new Date();
        return today.toISOString().split("T")[0];
    };
    const [buffetTimesBetweenShowings, setBuffetTimesBetweenShowings] = useState("");
    const [overlapingAllow, setOverlapingAllow] = useState(false);
    const [realTimeUpdates, setRealTimeUpdates] = useState(false);
    const [buyerReminders, setBuyerReminders] = useState(false);
    // const [hasChanges, setHasChanges] = useState(false);

    const getShowingSettings = async () => {
        try {
            const url = "/property-services/get_showing_settings/" + property?.property_id;
            const apiResponse = await getApi(url);
            const response = apiResponse?.response?.data;

            if (response.settings) {
                setOverlapingAllow(response.settings.allowOverlappingAppointments);
                setAppointmentDuration(response.settings.generalAppointmentSettings.appointmentDuration.duration);
                setAppointmentType(response.settings.appointmentType);
                setBuffetTimesBetweenShowings(response.settings.generalAppointmentSettings.bufferTimeBetweenShowings.duration);
                setMaxAppointmentLength(response.settings.generalAppointmentSettings.maxAppointmentLength.duration);
                setMinimumNoticeRequired(response.settings.generalAppointmentSettings.minimumNoticeRequired.hours);
                setIsPauseAvailability(response.settings.availabilitySchedule.PauseAvailability);
                setAccessType(response.settings.accessInformation.accessType);
                setAccessInstructions(response.settings.accessInformation.accessInstructions);
                setApprovalMethod(response.settings.appointmentApprovalSettings.approvalMethod);
                setNotificationPreferences(response.settings.appointmentApprovalSettings.notificationPreferences);
                setShowingInstructions({
                    preShowing: response.settings.showingInstructionsAndFeedback.instructions.preShowing,
                    postShowing: response.settings.showingInstructionsAndFeedback.instructions.postShowing,
                    onSite: response.settings.showingInstructionsAndFeedback.instructions.onSite,
                });
                setRealTimeUpdates(response.settings.notificationsAndReminders.realTimeUpdates.enabled ? true : false);
                setBuyerReminders(response.settings.notificationsAndReminders.buyerReminders.enabled);
                const weeklyScheduleObject = response.settings.availabilitySchedule.weeklySchedule;
                const updatedWeeklySchedule = availabilityArray.map((day) => {
                    const dayData = weeklyScheduleObject[day.day];
                    return {
                        ...day,
                        isAvailable: dayData.isAvailable,
                        startTime: dayData.startTime,
                        endTime: dayData.endTime,
                    };
                });
                setWeeklySchedule(updatedWeeklySchedule);
                setTemporaryUnavailability(response.settings.availabilitySchedule.temporaryUnavailability);
            }
        } catch (error) {
            console.log("🚀 ~ getShowingSettings ~ error:", error);
        }
    };
    useEffect(() => {
        getShowingSettings();
    }, []);
    // useEffect(
    //     () => {
    //         setHasChanges(true);
    //     },
    //     {
    //         appointmentType,
    //         appointmentDuration,
    //         minimumNoticeRequired,
    //         weeklySchedule,
    //         maxAppointmentLength,
    //         isPauseAvailability,
    //         accessType,
    //         approvalMethod,
    //         accessInstructions,
    //         autoDenyRequestsOutsideHours,
    //         notificationPreferences,
    //         showingInstructions,
    //         buffetTimesBetweenShowings,
    //         overlapingAllow,
    //         realTimeUpdates,
    //         buyerReminders,
    //     }
    // );
    const onSaveSettings = async () => {
        setIsLoading(true);
        try {
            const weeklyScheduleObject = weeklySchedule.reduce((acc, day) => {
                acc[day.day] = {
                    isAvailable: day.isAvailable,
                    startTime: day.startTime,
                    endTime: day.endTime,
                };
                return acc;
            }, {});
            const payload = {
                generalAppointmentSettings: {
                    appointmentType: appointmentType,
                    appointmentDuration: {
                        duration: Number(appointmentDuration || 30), // in minutes
                    },
                    minimumNoticeRequired: {
                        hours: minimumNoticeRequired, // 24-hour advance notice
                    },
                    maxAppointmentLength: {
                        duration: maxAppointmentLength, // in minutes
                    },
                    bufferTimeBetweenShowings: {
                        duration: buffetTimesBetweenShowings, // in minutes
                    },
                    allowOverlappingAppointments: overlapingAllow,
                },

                availabilitySchedule: {
                    weeklySchedule: weeklyScheduleObject,
                    temporaryUnavailability: [temporaryUnavailability],
                    notes: temporaryUnavailability.notes,
                    PauseAvailability: isPauseAvailability,
                },

                accessInformation: {
                    accessType: accessType,
                    accessInstructions: accessInstructions,
                },

                appointmentApprovalSettings: {
                    approvalMethod: approvalMethod,
                    notificationPreferences: notificationPreferences,
                    autoDenyRequestsOutsideHours: autoDenyRequestsOutsideHours,
                },

                showingInstructionsAndFeedback: {
                    instructions: showingInstructions,
                    feedbackCollection: {
                        enabled: true,
                        customQuestions: [], // Empty array (can add questions later)
                    },
                },

                notificationsAndReminders: {
                    buyerReminders: {
                        enabled: buyerReminders,
                    },
                    realTimeUpdates: {
                        enabled: realTimeUpdates,
                        types: {
                            cancellations: true,
                            requests: true,
                            noShows: true,
                        },
                    },
                    sellerControls: {
                        enabled: true,
                        allowedActions: {
                            reschedule: true,
                            cancel: true,
                            pauseAvailability: true,
                        },
                    },
                },
            };
            const url = "/property-services/update_showing_settings/" + property?.property_id;
            const response = await putApi(url, payload);
            toast.success("Showing settings updated successfully!", { toastId: "showingSettingsSuccess" });
            onClose();
            console.log("🚀 ~ onSaveSettings ~ response:", response);
        } catch (error) {
            console.log("🚀 ~ onSaveSettings ~ error:", error);
        } finally {
            setIsLoading(false);
            getShowingSettings();
        }
    };

    const handleStartTimeChange = (e, index) => {
        const newWeeklySchedule = [...weeklySchedule];
        newWeeklySchedule[index].startTime = e.target.valu;
        setWeeklySchedule(newWeeklySchedule);
    };
    const handleEndTimeChange = (e, index) => {
        const newWeeklySchedule = [...weeklySchedule];
        newWeeklySchedule[index].endTime = e.target.valu;
        setWeeklySchedule(newWeeklySchedule);
    };

    const handleDayClick = (index) => {
        const newWeeklySchedule = [...weeklySchedule];
        newWeeklySchedule[index].isAvailable = !newWeeklySchedule[index].isAvailable;
        setWeeklySchedule(newWeeklySchedule);
    };
    return (
        <div style={{ zIndex: 999 }} className="tw-fixed tw-top-0 tw-left-0 tw-w-screen tw-flex tw-items-center tw-justify-center ">
            <div className="tw-bg-white tw-rounded-[10px] tw-p-10 tw-w-[80%] 2xl:tw-w-[65%] tw-mt-[2%] tw-border tw-border-[#D0D5DD] tw-h-[80vh] tw-h-[95vh] md:tw-shadow-2xl tw-overflow-auto">
                <div className="tw-flex tw-justify-end tw-w-full">
                    <button
                        onClick={onClose}
                        className="tw-bg-[#EAEEF4] tw-w-[42px] tw-h-[42px] tw-flex tw-justify-center tw-items-center tw-rounded-full"
                    >
                        <RxCross1 />
                    </button>
                </div>
                <div className="tw-flex tw-gap-5 tw-items-center">
                    <h3 className="tw-text-[23px] tw-font-[700] tw-text-black">1. General Appointment Settings</h3>
                    <IoIosInformationCircleOutline size={24} />
                </div>
                <p className="tw-text-[#989898]">
                    Customize how appointments are scheduled for your listings by setting preferences like duration, notice requirements, buffer
                    times, and overlapping rules.
                </p>
                <div className="tw-flex tw-flex-col xl:tw-flex-row tw-justify-between tw-items-center tw-mt-5 tw-w-full">
                    <div className="tw-w-full xl:tw-w-[31%] tw-flex tw-flex-col tw-gap-3">
                        <p className="tw-text-black tw-font-[700]">Appointment Type</p>
                        <div className="tw-border tw-border-[#D0D5DD] tw-w-full tw-rounded-[10px] tw-px-5 tw-py-2">
                            <select
                                value={appointmentType}
                                onChange={(e) => setAppointmentType(e.target.value)}
                                className="tw-w-full tw-outline-none"
                            >
                                {appointmentTypes.map((item, index) => (
                                    <option key={index} value={item.value}>
                                        {" "}
                                        {item.label}{" "}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>
                    <div className="tw-w-full xl:tw-w-[31%] tw-flex tw-flex-col tw-gap-3">
                        <p className="tw-text-black tw-font-[700]">Appointment Duration</p>
                        <div className="tw-border tw-border-[#D0D5DD] tw-w-full tw-rounded-[10px] tw-px-5 tw-py-2">
                            <select
                                value={appointmentDuration}
                                onChange={(e) => setAppointmentDuration(e.target.value)}
                                className="tw-w-full tw-outline-none"
                            >
                                {appointmentDurationsArray.map((item, index) => (
                                    <option key={index} value={item.value}>
                                        {" "}
                                        {item.label}{" "}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>
                    <div className="tw-w-full xl:tw-w-[31%] tw-flex tw-flex-col tw-gap-3">
                        <p className="tw-text-black tw-font-[700]">Minimum Notice Required</p>
                        <div className="tw-border tw-border-[#D0D5DD] tw-w-full tw-rounded-[10px] tw-px-5 tw-py-2">
                            <select
                                value={minimumNoticeRequired}
                                onChange={(e) => setMinimumNoticeRequired(e.target.value)}
                                className="tw-w-full tw-outline-none"
                            >
                                {minimumNoticeRequiredArray.map((item, index) => (
                                    <option key={index} value={item.value}>
                                        {" "}
                                        {item.label}{" "}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>
                </div>
                <div className="tw-flex tw-flex-col xl:tw-flex-row tw-justify-between tw-items-center tw-mt-5 tw-w-full">
                    <div className="tw-w-full xl:tw-w-[31%] tw-flex tw-flex-col tw-gap-3">
                        <p className="tw-text-black tw-font-[700]">Max Appointment Length</p>
                        <div className="tw-border tw-border-[#D0D5DD] tw-w-full tw-rounded-[10px] tw-px-5 tw-py-2">
                            <select
                                value={maxAppointmentLength}
                                onChange={(e) => setMaxAppointmentLength(e.target.value)}
                                className="tw-w-full tw-outline-none"
                            >
                                {maxAppointmentLengthArray.map((item, index) => (
                                    <option key={index} value={item.value}>
                                        {" "}
                                        {item.label}{" "}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>
                    <div className="tw-w-full xl:tw-w-[31%] tw-flex tw-flex-col tw-gap-3">
                        <p className="tw-text-black tw-font-[700]">Buffer Time Between Showings</p>
                        <div className="tw-border tw-border-[#D0D5DD] tw-w-full tw-rounded-[10px] tw-px-5 tw-py-2">
                            <select
                                onChange={(e) => setBuffetTimesBetweenShowings(e.target.value)}
                                value={buffetTimesBetweenShowings}
                                className="tw-w-full tw-outline-none"
                            >
                                {buffetTimesBetweenShowingsArray.map((item, index) => (
                                    <option key={index} value={item.value}>
                                        {" "}
                                        {item.label}{" "}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>
                    <div className="tw-w-full xl:tw-w-[31%] tw-flex tw-gap-3 tw-items-center">
                        <p className="tw-text-black tw-font-[700]">Allow Overlapping Appointments</p>
                        <Switch value={overlapingAllow} checked={overlapingAllow} onChange={(e) => setOverlapingAllow(e.target.checked)} />
                    </div>
                </div>
                <div className="tw-flex tw-gap-5 tw-items-center tw-mt-20">
                    <h3 className="tw-text-[23px] tw-font-[700] tw-text-black">2. Availability Schedule</h3>
                    <IoIosInformationCircleOutline size={24} />
                </div>
                <p className="tw-text-[#989898]">
                    Set the days and times when showings are allowed, add exceptions for temporary unavailability, and enable seller control over
                    availability.
                </p>
                <div className="tw-flex tw-flex-col xl:tw-flex-row tw-gap-5 tw-justify-between tw-mt-10 tw-items-start">
                    <div className="tw-w-full xl:tw-w-[31%] ">
                        {weeklySchedule.map((item, index) => (
                            <div key={item.day} className="tw-flex tw-flex-col md:tw-flex-row md:tw-items-center tw-gap-3 tw-justify-between tw-mt-5">
                                <div className="tw-flex tw-gap-3 tw-items-center">
                                    <div
                                        onClick={() => handleDayClick(index)}
                                        className="tw-border tw-border-[#D0D5DD] tw-w-[20px] tw-h-[20px] tw-rounded-[5px] tw-flex tw-items-center tw-justify-center"
                                    >
                                        {item.isAvailable && <div className="tw-w-[10px] tw-h-[10px] tw-rounded-[2pxfjowjeo] tw-bg-primary"></div>}
                                    </div>
                                    <p className="tw-text-black tw-font-[700]">{item.day}</p>
                                </div>
                                <div className="tw-flex tw-gap-3 tw-ml-7 md:tw-ml-0">
                                    <input
                                        type="time"
                                        value={item.startTime}
                                        onChange={(e) => handleStartTimeChange(e, index)}
                                        className="tw-border tw-border-[#D0D5DD] tw-w-[80px] tw-h-[20px] tw-rounded-[5px] tw-outline-none"
                                    />
                                    <input
                                        type="time"
                                        value={item.endTime}
                                        onChange={(e) => handleEndTimeChange(e, index)}
                                        className="tw-border tw-border-[#D0D5DD] tw-w-[80px] tw-h-[20px] tw-rounded-[5px] tw-outline-none"
                                    />
                                </div>
                            </div>
                        ))}
                    </div>
                    <div className="tw-w-full xl:tw-w-[31%]">
                        <p className="tw-text-black tw-font-[700]">Temporary Unavailability</p>
                        <p className="tw-text-[#989898] tw-text-sm tw-mt-2">
                            Select dates when the property will be temporarily unavailable for showings
                        </p>
                        <div className="tw-flex tw-flex-col sm:tw-flex-row tw-gap-3 tw-mt-5">
                            <div className="tw-flex tw-flex-col tw-gap-1">
                                <label className="tw-text-xs tw-text-gray-600 tw-font-medium">Start Date</label>
                                <input
                                    type="date"
                                    value={temporaryUnavailability.startDate}
                                    min={getTodayDate()}
                                    placeholder="Start Date"
                                    title="Select start date for temporary unavailability"
                                    onChange={(e) => setTemporaryUnavailability({ ...temporaryUnavailability, startDate: e.target.value })}
                                    className="tw-border tw-border-[#D0D5DD] tw-p-2 tw-w-full sm:tw-w-[160px] tw-h-[40px] sm:tw-h-[30px] tw-rounded-[5px] tw-outline-none tw-text-sm"
                                />
                            </div>
                            <div className="tw-flex tw-flex-col tw-gap-1">
                                <label className="tw-text-xs tw-text-gray-600 tw-font-medium">End Date</label>
                                <input
                                    type="date"
                                    value={temporaryUnavailability.endDate}
                                    min={temporaryUnavailability.startDate || getTodayDate()}
                                    placeholder="End Date"
                                    title="Select end date for temporary unavailability"
                                    onChange={(e) => setTemporaryUnavailability({ ...temporaryUnavailability, endDate: e.target.value })}
                                    className="tw-border tw-border-[#D0D5DD] tw-p-2 tw-w-full sm:tw-w-[160px] tw-h-[40px] sm:tw-h-[30px] tw-rounded-[5px] tw-outline-none tw-text-sm"
                                />
                            </div>
                        </div>
                        <textarea
                            name=""
                            id=""
                            value={temporaryUnavailability.notes}
                            onChange={(e) => setTemporaryUnavailability({ ...temporaryUnavailability, notes: e.target.value })}
                            placeholder="Notes"
                            className="tw-w-full tw-border tw-border-[#D0D5DD] tw-rounded-[10px] tw-mt-5 tw-h-[121px] tw-p-5"
                        ></textarea>
                    </div>
                    <div className="tw-w-full xl:tw-w-[31%] tw-flex tw-gap-3 tw-items-center">
                        <p className="tw-text-black tw-font-[700]">Seller Control: Pause Availability</p>
                        <Switch
                            value={isPauseAvailability}
                            checked={isPauseAvailability}
                            onChange={(e) => setIsPauseAvailability(e.target.checked)}
                        />
                    </div>
                </div>
                <div className="tw-flex tw-flex-col xl:tw-flex-row tw-w-full tw-justify-between">
                    <div className="tw-w-full xl:tw-w-[40%]">
                        <div className="tw-flex tw-gap-5 tw-items-center tw-mt-20">
                            <h3 className="tw-text-[23px] tw-font-[700] tw-text-black">3. Access Information</h3>
                            <IoIosInformationCircleOutline size={24} />
                        </div>
                        <p className="tw-text-[#989898]">
                            Set the days and times when showings are allowed, add exceptions for temporary unavailability, and enable seller control
                            over availability.
                        </p>
                        <div className="tw-w-full tw-flex tw-flex-col tw-gap-3">
                            <p className="tw-text-black tw-font-[700]">Access Type Selector</p>
                            <div className="tw-border tw-border-[#D0D5DD] tw-w-full tw-rounded-[10px] tw-px-5 tw-py-2">
                                <select value={accessType} onChange={(e) => setAccessType(e.target.value)} className="tw-w-full tw-outline-none">
                                    {accessTypeArray.map((item, index) => (
                                        <option key={index} value={item.value}>
                                            {item.label}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>
                        <div className="tw-w-full tw-flex tw-flex-col tw-gap-3">
                            <p className="tw-text-black tw-font-[700]">Access Instructions</p>
                            <textarea
                                name=""
                                id=""
                                value={accessInstructions}
                                onChange={(e) => setAccessInstructions(e.target.value)}
                                placeholder="Notes"
                                className="tw-w-full tw-border tw-border-[#D0D5DD] tw-rounded-[10px] tw-mt-5 tw-h-[121px] tw-p-5"
                            ></textarea>
                        </div>
                    </div>
                    <div className="tw-w-full xl:tw-w-[40%]">
                        <div className="tw-flex tw-gap-5 tw-items-center tw-mt-20">
                            <h3 className="tw-text-[23px] tw-font-[700] tw-text-black">3. Access Information</h3>
                            <IoIosInformationCircleOutline size={24} />
                        </div>
                        <p className="tw-text-[#989898]">
                            Set the days and times when showings are allowed, add exceptions for temporary unavailability, and enable seller control
                            over availability.
                        </p>
                        <p className="tw-text-black tw-font-[700] tw-mt-5">Approval Method</p>
                        <div className="tw-flex tw-items-center tw-gap-2 tw-m-3">
                            <div
                                onClick={() => setApprovalMethod("MANUAL")}
                                className="tw-h-[20px] tw-w-[20px] tw-rounded-full tw-border-5 tw-border-[#D0D5DD] tw-flex tw-justify-center tw-items-center"
                            >
                                {approvalMethod === "MANUAL" && <div className="tw-h-[10px] tw-w-[10px] tw-rounded-full tw-bg-primary" />}
                            </div>
                            <p className="tw-text-black ">Manual</p>
                        </div>
                        <div className="tw-flex tw-items-center tw-gap-2 tw-m-3">
                            <div
                                onClick={() => setApprovalMethod("AUTO_APPROVE")}
                                className="tw-h-[20px] tw-w-[20px] tw-rounded-full tw-border-5 tw-border-[#D0D5DD] tw-flex tw-justify-center tw-items-center"
                            >
                                {approvalMethod === "AUTO_APPROVE" && <div className="tw-h-[10px] tw-w-[10px] tw-rounded-full tw-bg-primary" />}
                            </div>
                            <p className="tw-text-black ">Instant (Within hour)</p>
                        </div>
                        <p className="tw-text-black tw-font-[700] tw-mt-5">Notification Preferences</p>
                        <div className="tw-flex tw-gap-3 tw-items-center tw-m-3">
                            <div
                                onClick={() =>
                                    setNotificationPreferences({
                                        ...notificationPreferences,
                                        sms: !notificationPreferences.sms,
                                    })
                                }
                                className="tw-border tw-border-[#D0D5DD] tw-w-[20px] tw-h-[20px] tw-rounded-[5px] tw-flex tw-items-center tw-justify-center"
                            >
                                {notificationPreferences.sms && <div className="tw-h-[15px] tw-w-[15px] tw-rounded-[2px] tw-bg-primary" />}
                            </div>
                            <p className="tw-text-black tw-font-[700]">Text</p>
                        </div>
                        <div className="tw-flex tw-gap-3 tw-items-center tw-m-3">
                            <div
                                onClick={() =>
                                    setNotificationPreferences({
                                        ...notificationPreferences,
                                        email: !notificationPreferences.email,
                                    })
                                }
                                className="tw-border tw-border-[#D0D5DD] tw-w-[20px] tw-h-[20px] tw-rounded-[5px] tw-flex tw-items-center tw-justify-center"
                            >
                                {notificationPreferences.email && <div className="tw-h-[15px] tw-w-[15px] tw-rounded-[2px] tw-bg-primary" />}
                            </div>
                            <p className="tw-text-black tw-font-[700]">Email</p>
                        </div>
                        <div className="tw-flex tw-gap-3 tw-items-center tw-m-3">
                            <div
                                onClick={() =>
                                    setNotificationPreferences({
                                        ...notificationPreferences,
                                        pushNotification: !notificationPreferences.pushNotification,
                                    })
                                }
                                className="tw-border tw-border-[#D0D5DD] tw-w-[20px] tw-h-[20px] tw-rounded-[5px] tw-flex tw-items-center tw-justify-center"
                            >
                                {notificationPreferences.pushNotification && (
                                    <div className="tw-h-[15px] tw-w-[15px] tw-rounded-[2px] tw-bg-primary" />
                                )}
                            </div>
                            <p className="tw-text-black tw-font-[700]">Push</p>
                        </div>
                        <div className="tw-flex tw-gap-3 tw-items-center tw-m-3">
                            <div
                                onClick={() =>
                                    setNotificationPreferences({
                                        ...notificationPreferences,
                                        inAppOnly: !notificationPreferences.inAppOnly,
                                    })
                                }
                                className="tw-border tw-border-[#D0D5DD] tw-w-[20px] tw-h-[20px] tw-rounded-[5px] tw-flex tw-items-center tw-justify-center"
                            >
                                {notificationPreferences.inAppOnly && <div className="tw-h-[15px] tw-w-[15px] tw-rounded-[2px] tw-bg-primary" />}
                            </div>
                            <p className="tw-text-black tw-font-[700]">In app notification</p>
                        </div>
                        <div className="tw-w-full tw-flex tw-gap-3 tw-items-center tw-mt-3">
                            <p className="tw-text-black tw-font-[700]">Auto-Deny Requests Outside Available Hours:</p>
                            <Switch
                                value={autoDenyRequestsOutsideHours}
                                checked={autoDenyRequestsOutsideHours}
                                onChange={(e) => setAutoDenyRequestsOutsideHours(e.target.checked)}
                            />
                        </div>
                    </div>
                </div>
                <div className="tw-flex tw-gap-5 tw-items-center tw-mt-10">
                    <h3 className="tw-text-[23px] tw-font-[700] tw-text-black">5. Showing Instructions & Feedback</h3>
                    <IoIosInformationCircleOutline size={24} />
                </div>
                <p className="tw-text-[#989898]">
                    Share instructions for agents before, during, and after the showing to ensure a smooth experience.
                </p>
                <div className="tw-flex tw-flex-col xl:tw-flex-row tw-justify-between tw-mt-5">
                    <div className="tw-w-full xl:tw-w-[31%] tw-flex tw-flex-col tw-gap-3">
                        <p className="tw-text-black tw-font-[700]">Pre-showing Instructions</p>
                        <textarea
                            name=""
                            id=""
                            value={showingInstructions.preShowing}
                            onChange={(e) => setShowingInstructions({ ...showingInstructions, preShowing: e.target.value })}
                            placeholder="Notes"
                            className="tw-w-full tw-border tw-border-[#D0D5DD] tw-rounded-[10px] tw-mt-5 tw-h-[121px] tw-p-5"
                        ></textarea>
                    </div>
                    <div className="tw-w-full xl:tw-w-[31%] tw-flex tw-flex-col tw-gap-3">
                        <p className="tw-text-black tw-font-[700]">On-site Instructions</p>
                        <textarea
                            name=""
                            id=""
                            value={showingInstructions.onSite}
                            onChange={(e) => setShowingInstructions({ ...showingInstructions, onSite: e.target.value })}
                            placeholder="Notes"
                            className="tw-w-full tw-border tw-border-[#D0D5DD] tw-rounded-[10px] tw-mt-5 tw-h-[121px] tw-p-5"
                        ></textarea>
                    </div>
                    <div className="tw-w-full xl:tw-w-[31%] tw-flex tw-flex-col tw-gap-3">
                        <p className="tw-text-black tw-font-[700]">Post-showing Instructions</p>
                        <textarea
                            name=""
                            id=""
                            value={showingInstructions.postShowing}
                            onChange={(e) => setShowingInstructions({ ...showingInstructions, postShowing: e.target.value })}
                            placeholder="Notes"
                            className="tw-w-full tw-border tw-border-[#D0D5DD] tw-rounded-[10px] tw-mt-5 tw-h-[121px] tw-p-5"
                        ></textarea>
                    </div>
                </div>
                <div className="tw-flex tw-gap-5 tw-items-center tw-mt-5">
                    <h3 className="tw-text-[23px] tw-font-[700] tw-text-black">6. Notifications & Reminders</h3>
                    <IoIosInformationCircleOutline size={24} />
                </div>
                <p className="tw-text-[#989898]">Manage how and when buyers receive appointment updates and reminders. </p>
                <div className="tw-flex tw-gap-5 tw-mt-3 tw-items-start">
                    <div>
                        <div className="tw-flex tw-gap-3 tw-items-center">
                            <p className="tw-text-black tw-font-[700]">Allow Buyer Reminders</p>
                            <Switch
                                value={buyerReminders}
                                checked={buyerReminders ? true : false}
                                onChange={(e) => setBuyerReminders(e.target.checked)}
                            />
                        </div>
                        <p className="tw-text-[#8D8D8D] tw-text-[12px]"> 1 hour before</p>
                    </div>
                    <div className="tw-flex tw-gap-3 tw-items-center">
                        <p className="tw-text-black tw-font-[700]">Allow Real Time Updates</p>
                        <Switch
                            value={realTimeUpdates}
                            checked={realTimeUpdates ? true : false}
                            onChange={(e) => setRealTimeUpdates(e.target.checked)}
                        />
                    </div>
                </div>
                <div className="tw-flex tw-justify-end tw-w-full   tw-items-center tw-gap-4 tw-mb-5 tw-mt-10">
                    <button
                        onClick={() => onSaveSettings()}
                        className="tw-bg-primary  hover:tw-bg-primary_hover max-sm:tw-w-full tw-text-white tw-px-[2rem] tw-py-2 tw-rounded-lg"
                    >
                        Save Settings
                    </button>
                    <button
                        onClick={() => onClose()}
                        className="tw-bg-[#fff]   max-sm:tw-w-full tw-border tw-border-primary tw-text-primary hover:tw-border-secondary tw-px-5 tw-py-2 tw-rounded-lg"
                    >
                        Cancel
                    </button>
                </div>
            </div>
        </div>
    );
}
